// FormBuilderDropGap — one persistent drop target of the form builder. Owns
// the whole drop contract in one place (the MIME gate, hover highlight,
// payload validation) for every variant: the spacing gaps between canvas
// items, the gaps between a section's children, a section's empty "Drag
// fields here" panel, and the empty-canvas placeholder. Each gap handles its
// own drag events (no container-level hit-testing), so a zone expands exactly
// while the drag hovers it and collapses when it leaves.
//
// `accept` gates by dragged kind during dragover — which can only read
// `dataTransfer.types`, hence the SECTION_DRAG_MIME flavor — and is
// re-verified on drop against the real payload. Sections can't nest, and
// fields after the first section must live inside one, so callers pass
// 'section' / 'field' / 'any' per position.
//
// Two drag families share the gaps, told apart by `types` alone: a library
// ADD (FIELD_DRAG_MIME, payload = element type → onDropField) and a canvas
// REORDER (MOVE_DRAG_MIME, payload = the moved row's client id →
// onMoveField). The caller still binds the insert position for both; a gap
// without onMoveField (the empty canvas) simply rejects moves, and `disabled`
// lets the canvas switch off the gaps hugging the dragged block (their drop
// would be a no-op).

import { useEffect, useState, type DragEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FIELD_DRAG_MIME, FIELD_LIBRARY, MOVE_DRAG_MIME, SECTION_DRAG_MIME } from './utils/fieldLibrary';
import type { LibraryElementType } from './utils/formElementDefaults';

interface FormBuilderDropGapProps {
  /** Which dragged kinds may land here. */
  accept: 'any' | 'field' | 'section';
  /** A valid library item was dropped — the caller binds the insert position. */
  onDropField: (type: LibraryElementType) => void;
  /** An existing row was dropped — same position binding; omitting it makes
   *  the gap reject reorder drags outright. */
  onMoveField?: (id: string) => void;
  /** Reject everything (the gaps hugging the dragged block — a no-op drop). */
  disabled?: boolean;
  /** BEM block of the element (default: the canvas spacing gap). */
  block?: string;
  /** Extra classes (edge variants etc.), appended as-is. */
  className?: string;
  /** Content override; receives the hover state. Default: the "Drop Here"
   *  label (aria-hidden — a purely visual affordance). */
  children?: (hovering: boolean) => ReactNode;
}

const FormBuilderDropGap = ({
  accept,
  onDropField,
  onMoveField,
  disabled = false,
  block = 'form-builder-canvas__drop-gap',
  className,
  children,
}: FormBuilderDropGapProps) => {
  const { t } = useTranslation();
  const [hovering, setHovering] = useState(false);

  // Escape-cancel and out-of-window release never fire this gap's dragleave —
  // but the SOURCE's dragend (and any same-document drop) always fires, so a
  // document-level listener while highlighted covers every way a drag ends.
  useEffect(() => {
    if (!hovering) return undefined;
    const clear = () => setHovering(false);
    document.addEventListener('dragend', clear);
    document.addEventListener('drop', clear);
    return () => {
      document.removeEventListener('dragend', clear);
      document.removeEventListener('drop', clear);
    };
  }, [hovering]);

  const matches = (e: DragEvent) => {
    if (disabled) return false;
    const types = e.dataTransfer.types;
    const isMove = types.includes(MOVE_DRAG_MIME);
    if (isMove ? !onMoveField : !types.includes(FIELD_DRAG_MIME)) return false;
    const isSection = types.includes(SECTION_DRAG_MIME);
    return accept === 'any' || (accept === 'section') === isSection;
  };

  const handleDragOver = (e: DragEvent) => {
    if (!matches(e)) return;
    e.preventDefault(); // allow the drop
    // Must mirror the source's effectAllowed — Chrome cancels a drop whose
    // dropEffect the source disallows ('copy' against a move drag).
    e.dataTransfer.dropEffect = e.dataTransfer.types.includes(MOVE_DRAG_MIME) ? 'move' : 'copy';
    setHovering(true);
  };
  const handleDragLeave = (e: DragEvent) => {
    // Ignore leaves into our own children (they'd flicker the highlight).
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setHovering(false);
  };
  const handleDrop = (e: DragEvent) => {
    if (!matches(e)) return;
    e.preventDefault();
    setHovering(false);
    // The gate above trusts `types`; the payloads are only readable now.
    const moveId = e.dataTransfer.getData(MOVE_DRAG_MIME);
    if (moveId) {
      onMoveField?.(moveId); // the move op re-guards everything
      return;
    }
    const type = e.dataTransfer.getData(FIELD_DRAG_MIME);
    if (!FIELD_LIBRARY.some((f) => f.type === type)) return;
    if (accept !== 'any' && (accept === 'section') !== (type === 'section')) return;
    onDropField(type as LibraryElementType);
  };

  return (
    <div
      className={[block, hovering && `${block}--active`, className].filter(Boolean).join(' ')}
      aria-hidden={children ? undefined : true}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {children ? children(hovering) : t('forms_builder_drop_here')}
    </div>
  );
};

export default FormBuilderDropGap;
